import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { shortOperatorLabel } from '@/lib/disruptions/operator-stamp';
import { useOperatorActivity, useOperatorActivityDownload } from '@/lib/operator-activity/use-operator-activity';
import { OPERATOR_ACTION_KINDS, type OperatorActivityReport, type OperatorActivityRow } from '@/lib/operator-activity/types';

// Disruption control-center "Actividad por operador" tab (#813, SKY PoC §8
// "trabajo manual" / "carga del administrador"): per operator, how many desk
// marks of each kind, first and last; the disruption's first / last / last-
// passenger instants (effective time); the pre-attribution and passenger-made
// marks said apart; the marks themselves as the audit twin; CSV download.

interface OperatorActivityTabProps {
  disruptionUrn: string;
  enabled: boolean;
}

function clock(iso: string | null, locale: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

/** "1h 25m" between two instants; `null` when either is missing. */
export function spanLabel(from: string | null, to: string | null): string | null {
  if (!from || !to) return null;
  const minutes = Math.max(0, Math.round((new Date(to).getTime() - new Date(from).getTime()) / 60_000));
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
}

export function operatorLabel(row: Pick<OperatorActivityRow, 'operatorUrn' | 'name'>, unnamed: string): { primary: string; secondary: string | null } {
  if (row.name) return { primary: row.name, secondary: shortOperatorLabel(row.operatorUrn) };
  return { primary: shortOperatorLabel(row.operatorUrn) || unnamed, secondary: null };
}

export function OperatorActivityTab({ disruptionUrn, enabled }: OperatorActivityTabProps) {
  const { t, i18n } = useTranslation('disruptions');
  const { data, isLoading, isError } = useOperatorActivity(disruptionUrn, enabled);
  const download = useOperatorActivityDownload(disruptionUrn);
  const [showMarks, setShowMarks] = useState(false);

  if (isLoading) {
    return (
      <div className="space-y-3" data-testid="operator-activity-loading">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }
  if (isError || !data) {
    return (
      <Card>
        <CardContent className="py-6 text-sm text-destructive">{t('operatorActivity.error')}</CardContent>
      </Card>
    );
  }

  const unattributedTotal = OPERATOR_ACTION_KINDS.reduce((n, k) => n + (data.unattributed[k] ?? 0), 0);
  const locale = i18n.language;
  // Effective time: from the first desk mark to the last passenger check-in (the §8
  // window); falls back to the last desk mark when nobody is checked in yet.
  const span = spanLabel(data.firstAt, data.lastPassengerAt ?? data.lastAt);

  return (
    <div className="flex flex-col gap-4" data-testid="operator-activity-tab">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">{t('operatorActivity.title')}</h2>
          <p className="text-sm text-muted-foreground">
            {t('operatorActivity.subtitle', { when: new Date(data.generatedAt).toLocaleString(locale) })}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => download.mutate()}
          disabled={download.isPending}
          data-testid="operator-activity-download"
        >
          {download.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden /> : <Download className="mr-1 h-4 w-4" aria-hidden />}
          {download.isPending ? t('operatorActivity.downloading') : t('operatorActivity.download')}
        </Button>
      </div>

      {!data.known.cases ? (
        <p
          className="flex items-center gap-2 rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-300"
          data-testid="operator-activity-unknown"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
          {t('operatorActivity.unknown')}
        </p>
      ) : null}

      <Kpis report={data} locale={locale} span={span} />

      {data.operators.length === 0 ? (
        <EmptyState title={t('operatorActivity.empty.title')} description={t('operatorActivity.empty.description')} />
      ) : (
        <Card>
          <CardContent className="overflow-x-auto p-0">
            <table className="w-full text-xs" data-testid="operator-activity-table">
              <thead className="bg-muted/40 text-left text-[11px] uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">{t('operatorActivity.columns.operator')}</th>
                  {OPERATOR_ACTION_KINDS.map((k) => (
                    <th key={k} className="px-2 py-2 text-right">{t(`operatorActivity.actions.${k}`)}</th>
                  ))}
                  <th className="px-2 py-2 text-right">{t('operatorActivity.columns.total')}</th>
                  <th className="px-2 py-2 text-right">{t('operatorActivity.columns.passengers')}</th>
                  <th className="px-2 py-2 text-right">{t('operatorActivity.columns.pnrs')}</th>
                  <th className="px-2 py-2">{t('operatorActivity.columns.firstAt')}</th>
                  <th className="px-2 py-2">{t('operatorActivity.columns.lastAt')}</th>
                </tr>
              </thead>
              <tbody>
                {data.operators.map((row) => {
                  const label = operatorLabel(row, t('operatorActivity.unnamed'));
                  return (
                    <tr key={row.operatorUrn} className="border-t border-border" data-testid="operator-activity-row">
                      <td className="px-3 py-2">
                        <div className="font-medium">{label.primary}</div>
                        {label.secondary ? <div className="font-mono text-[10px] text-muted-foreground">{label.secondary}</div> : null}
                      </td>
                      {OPERATOR_ACTION_KINDS.map((k) => (
                        <td key={k} className="px-2 py-2 text-right tabular-nums">
                          {row.actions[k] > 0 ? row.actions[k] : <span className="text-muted-foreground">·</span>}
                        </td>
                      ))}
                      <td className="px-2 py-2 text-right font-semibold tabular-nums">{row.total}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{row.passengers}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{row.pnrs}</td>
                      <td className="px-2 py-2 tabular-nums">{clock(row.firstAt, locale)}</td>
                      <td className="px-2 py-2 tabular-nums">{clock(row.lastAt, locale)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {unattributedTotal > 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="operator-activity-unattributed">
          {t('operatorActivity.notes.unattributed', { n: unattributedTotal })}
        </p>
      ) : null}
      {data.passengerDriven.checkIn > 0 || data.passengerDriven.accommodationOutcome > 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="operator-activity-passenger-driven">
          {t('operatorActivity.notes.passengerDriven', {
            checkIn: data.passengerDriven.checkIn,
            accommodation: data.passengerDriven.accommodationOutcome,
          })}
        </p>
      ) : null}

      {data.marks.length > 0 ? (
        <div className="flex flex-col gap-2">
          <button
            type="button"
            className="self-start text-xs text-primary underline-offset-2 hover:underline"
            onClick={() => setShowMarks((v) => !v)}
            data-testid="operator-activity-marks-toggle"
          >
            {showMarks ? t('operatorActivity.marks.hide') : t('operatorActivity.marks.show', { n: data.marks.length })}
          </button>
          {showMarks ? (
            <Card>
              <CardContent className="overflow-x-auto p-0">
                <table className="w-full text-xs" data-testid="operator-activity-marks">
                  <thead className="bg-muted/40 text-left text-[11px] uppercase text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2">{t('operatorActivity.marks.time')}</th>
                      <th className="px-2 py-2">{t('operatorActivity.marks.operator')}</th>
                      <th className="px-2 py-2">{t('operatorActivity.marks.action')}</th>
                      <th className="px-2 py-2">{t('operatorActivity.marks.pnr')}</th>
                      <th className="px-2 py-2">{t('operatorActivity.marks.passenger')}</th>
                      <th className="px-2 py-2">{t('operatorActivity.marks.value')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.marks.map((m, i) => (
                      <tr key={`${m.at}-${m.kind}-${m.subCaseUrn}-${m.paxUrn ?? i}`} className="border-t border-border">
                        <td className="px-3 py-1.5 tabular-nums">{clock(m.at, locale)}</td>
                        <td className="px-2 py-1.5">{m.operatorName ?? shortOperatorLabel(m.operatorUrn)}</td>
                        <td className="px-2 py-1.5">{t(`operatorActivity.actions.${m.kind}`)}</td>
                        <td className="px-2 py-1.5 font-mono">{m.pnr}</td>
                        <td className="px-2 py-1.5 font-mono text-[10px] text-muted-foreground">{m.paxUrn ? m.paxUrn.split(':')[2] ?? m.paxUrn : '—'}</td>
                        <td className="px-2 py-1.5">{m.value ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function Kpis({ report, locale, span }: { report: OperatorActivityReport; locale: string; span: string | null }) {
  const { t } = useTranslation('disruptions');
  const tiles: Array<{ key: string; label: string; value: string; hint?: string }> = [
    { key: 'actions', label: t('operatorActivity.kpi.actions'), value: String(report.totals.actions) },
    { key: 'operators', label: t('operatorActivity.kpi.operators'), value: String(report.totals.operators) },
    {
      key: 'checkedIn',
      label: t('operatorActivity.kpi.checkedIn'),
      value: String(report.totals.checkedIn),
      hint: t('operatorActivity.kpi.checkedInOf', { n: report.totals.checkedIn, total: report.totals.passengers }),
    },
    { key: 'firstAt', label: t('operatorActivity.kpi.firstAt'), value: clock(report.firstAt, locale) },
    { key: 'lastAt', label: t('operatorActivity.kpi.lastAt'), value: clock(report.lastAt, locale) },
    {
      key: 'lastPassengerAt',
      label: t('operatorActivity.kpi.lastPassengerAt'),
      value: clock(report.lastPassengerAt, locale),
      hint: t('operatorActivity.kpi.lastPassengerHint'),
    },
    { key: 'span', label: t('operatorActivity.kpi.span'), value: span ?? t('operatorActivity.kpi.none') },
  ];
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7" data-testid="operator-activity-kpis">
      {tiles.map((tile) => (
        <Card key={tile.key}>
          <CardContent className="p-3">
            <div className="text-[11px] uppercase text-muted-foreground">{tile.label}</div>
            <div className="text-lg font-semibold tabular-nums" data-testid={`operator-activity-kpi-${tile.key}`}>{tile.value}</div>
            {tile.hint ? <div className="text-[11px] text-muted-foreground">{tile.hint}</div> : null}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
